import React, { useState, useEffect } from 'react';
import { X, BarChart3, PieChart, TrendingUp, MapPin, Home, Users, DollarSign } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { propertyService } from '../../../lib/supabase';

export default function AnalyticsDashboard({ communes, properties, onClose }) {
  const [stats, setStats] = useState({
    communes: {
      total: 0,
      byDistrict: {},
      avgPopulation: 0,
      avgPrice: 0
    },
    properties: {
      total: 0,
      byStatus: {},
      byType: {},
      byCommune: {},
      avgPrice: 0,
      priceRange: { min: 0, max: 0 }
    },
    relationships: {
      propertiesPerCommune: 0,
      communesWithoutProperties: 0,
      highDensityCommunes: 0
    }
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    calculateStats();
  }, [communes, properties]);

  const calculateStats = async () => {
    try {
      setLoading(true);
      
      // Get property stats from service
      const propertyStats = await propertyService?.getPropertyStats();
      
      // Calculate commune stats
      const communeStats = {
        total: communes?.length || 0,
        byDistrict: {},
        avgPopulation: 0,
        avgPrice: 0
      };

      let totalPopulation = 0;
      let totalPrice = 0;
      let communesWithPrice = 0;

      communes?.forEach(commune => {
        const district = commune?.district || 'Non défini';
        communeStats.byDistrict[district] = (communeStats?.byDistrict?.[district] || 0) + 1;
        
        if (commune?.population) {
          totalPopulation += commune?.population;
        }
        
        if (commune?.average_price) {
          totalPrice += commune?.average_price;
          communesWithPrice++;
        }
      });

      communeStats.avgPopulation = communeStats?.total > 0 ? totalPopulation / communeStats?.total : 0;
      communeStats.avgPrice = communesWithPrice > 0 ? totalPrice / communesWithPrice : 0;

      // Calculate relationship stats
      const communesWithProperties = new Set();
      properties?.forEach(property => {
        if (property?.commune_id) {
          communesWithProperties?.add(property?.commune_id);
        }
      });

      const propertyCounts = {};
      properties?.forEach(property => {
        const communeId = property?.commune_id;
        if (communeId) {
          propertyCounts[communeId] = (propertyCounts?.[communeId] || 0) + 1;
        }
      });

      const relationshipStats = {
        propertiesPerCommune: communeStats?.total > 0 ? (properties?.length || 0) / communeStats?.total : 0,
        communesWithoutProperties: communeStats?.total - communesWithProperties?.size,
        highDensityCommunes: Object.values(propertyCounts)?.filter(count => count > 5)?.length || 0
      };

      setStats({
        communes: communeStats,
        properties: propertyStats,
        relationships: relationshipStats
      });
      
    } catch (error) {
      console.error('Error calculating stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    })?.format(price || 0);
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('fr-FR')?.format(number || 0);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-lg max-w-6xl w-full p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Calcul des statistiques...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold flex items-center">
            <BarChart3 className="w-6 h-6 mr-2 text-blue-600" />
            Tableau de Bord Analytique
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600">Total Communes</p>
                  <p className="text-2xl font-bold text-blue-900">{stats?.communes?.total}</p>
                </div>
                <MapPin className="w-8 h-8 text-blue-600" />
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600">Total Propriétés</p>
                  <p className="text-2xl font-bold text-green-900">{stats?.properties?.total}</p>
                </div>
                <Home className="w-8 h-8 text-green-600" />
              </div>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-600">Population Moy.</p>
                  <p className="text-2xl font-bold text-purple-900">
                    {formatNumber(Math.round(stats?.communes?.avgPopulation))}
                  </p>
                </div>
                <Users className="w-8 h-8 text-purple-600" />
              </div>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-600">Prix Moyen</p>
                  <p className="text-lg font-bold text-orange-900">
                    {formatPrice(stats?.properties?.avgPrice)}
                  </p>
                </div>
                <DollarSign className="w-8 h-8 text-orange-600" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Commune Distribution */}
            <div className="bg-white border rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <PieChart className="w-5 h-5 mr-2 text-blue-600" />
                Distribution par District
              </h3>
              
              <div className="space-y-3">
                {Object.entries(stats?.communes?.byDistrict || {})?.map(([district, count]) => (
                  <div key={district} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">{district}</span>
                    <div className="flex items-center">
                      <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                        <div 
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ 
                            width: `${(count / stats?.communes?.total) * 100}%` 
                          }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Property Status */}
            <div className="bg-white border rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <BarChart3 className="w-5 h-5 mr-2 text-green-600" />
                Statut des Propriétés
              </h3>
              
              <div className="space-y-3">
                {Object.entries(stats?.properties?.byStatus || {})?.map(([status, count]) => {
                  const colors = {
                    available: 'bg-green-600',
                    reserved: 'bg-yellow-600',
                    sold: 'bg-gray-600',
                    withdrawn: 'bg-red-600'
                  };
                  
                  const labels = {
                    available: 'Disponible',
                    reserved: 'Réservé',
                    sold: 'Vendu',
                    withdrawn: 'Retiré'
                  };
                  
                  return (
                    <div key={status} className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">{labels?.[status] || status}</span>
                      <div className="flex items-center">
                        <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                          <div 
                            className={`h-2 rounded-full ${colors?.[status] || 'bg-gray-600'}`}
                            style={{ 
                              width: `${(count / stats?.properties?.total) * 100}%` 
                            }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium">{count}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Property Types */}
            <div className="bg-white border rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <Home className="w-5 h-5 mr-2 text-purple-600" />
                Types de Propriétés
              </h3>
              
              <div className="space-y-3">
                {Object.entries(stats?.properties?.byType || {})?.map(([type, count]) => {
                  const labels = {
                    apartment: 'Appartement',
                    house: 'Maison',
                    villa: 'Villa',
                    land: 'Terrain',
                    commercial: 'Commercial'
                  };
                  
                  return (
                    <div key={type} className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">{labels?.[type] || type}</span>
                      <div className="flex items-center">
                        <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                          <div 
                            className="bg-purple-600 h-2 rounded-full"
                            style={{ 
                              width: `${(count / stats?.properties?.total) * 100}%` 
                            }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium">{count}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Relationship Insights */}
            <div className="bg-white border rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <TrendingUp className="w-5 h-5 mr-2 text-orange-600" />
                Insights Relationnels
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <span className="text-sm text-gray-700">Propriétés par commune</span>
                  <span className="text-lg font-semibold text-orange-600">
                    {stats?.relationships?.propertiesPerCommune?.toFixed(1)}
                  </span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-red-50 rounded">
                  <span className="text-sm text-gray-700">Communes sans propriétés</span>
                  <span className="text-lg font-semibold text-red-600">
                    {stats?.relationships?.communesWithoutProperties}
                  </span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded">
                  <span className="text-sm text-gray-700">Communes haute densité (&gt;5)</span>
                  <span className="text-lg font-semibold text-blue-600">
                    {stats?.relationships?.highDensityCommunes}
                  </span>
                </div>

                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
                  <h4 className="font-medium text-green-800 mb-2">Fourchette de Prix</h4>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-green-700">
                      Min: {formatPrice(stats?.properties?.priceRange?.min)}
                    </span>
                    <span className="text-green-700">
                      Max: {formatPrice(stats?.properties?.priceRange?.max)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t">
          <Button onClick={onClose}>
            Fermer
          </Button>
        </div>
      </div>
    </div>
  );
}